import React, { useState } from 'react';
import { View, Text, TextInput, ActivityIndicator } from 'react-native';
import {
  Search,
  ShieldCheck,
  AlertTriangle,
  RotateCcw,
  CalendarClock,
  Zap,
  FileCheck2,
  ChevronRight,
  Building2,
  Trash2,
  DoorOpen,
} from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { AppModal, ModalAction } from '@/components/modal';
import { AccountActionButton } from '@/components/account/AccountActionButton';
import { StepUpModal } from '@/components/account/StepUpModal';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb, noOutlineStyle } from '@/lib/platform';
import { handleError } from '@/lib/errors';
import {
  COMPANY_DATA_REASONS,
  ERASURE_REASONS,
  ErasureAdminError,
  companyActions,
  useLookupCompany,
  useReopenCompany,
  useRequestCompanyDataDeletion,
  useRequeueCompanyDataDeletion,
  type CompanyAdminState,
  type CompanyDataReason,
  useErasureProof,
  useErasureRequests,
  useFileErasure,
  useLookupAccount,
  useRequeueErasure,
  type ErasureFiled,
  type ErasureLookup,
  type ErasureReason,
  type ErasureRequestStatus,
  type ErasureRequestSummary,
} from '@/lib/admin/erasure-requests';
import { APP_LOCALE } from '@/lib/format';

/**
 * Erasure requests — the admin tool for data-subject requests that arrive by
 * email at privacy@ (Phase 118-09).
 *
 * Three jobs, top to bottom:
 *   1. Handle a request: look the address up, see WHICH account it resolves to,
 *      paste that id back to confirm, pick why, then schedule (30 days) or —
 *      only when the person asked for it — erase immediately.
 *   2. Watch the queue: pending / processing / partial / failed requests; a
 *      failed one can be re-queued (its company holds stay until it completes).
 *   3. Proof: the request and its erasure_events outcome, as text to send back.
 *
 * Nothing here shows an address or a name. The lookup answers with an id.
 *
 *   4. Closed companies (119-07): look a company up by id, then — for a SEALED
 *      company with its data in place and nobody in it — file the deletion of
 *      its data (no grace; the journey snapshot is written first and its score
 *      history is kept), reopen it for a returning founder (their own fresh
 *      verified claim is the proof, resolved by the server), or re-queue a
 *      failed data deletion. Each needs the pasted company id and a confirm.
 */

const MAX_WIDTH = 720;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(APP_LOCALE, { dateStyle: 'medium', timeStyle: 'short' });
}

function messageFor(err: unknown, context: string): string | null {
  if (err instanceof ErasureAdminError) return err.userMessage;
  const { message, isCancelled } = handleError(err, { context });
  return isCancelled ? null : message;
}

// ─────────────────────────────────────────────────────────────────────────────
// Small pieces
// ─────────────────────────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <View
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light, padding: isWeb ? 20 : 16, gap: 12 }}
    >
      {children}
    </View>
  );
}

function SectionTitle({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  const colors = useThemeColors();
  return (
    <View style={{ gap: 4 }}>
      <View className="flex-row items-center" style={{ gap: 8 }}>
        <View className="shrink-0">{icon}</View>
        <Text className="flex-1 text-lg font-bold" style={{ color: colors.text.primary }} numberOfLines={1}>
          {title}
        </Text>
      </View>
      {hint ? (
        <Text className="text-sm" style={{ color: colors.text.secondary }} numberOfLines={3}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

function StatusChip({ status }: { status: ErasureRequestStatus | 'completed' }) {
  const colors = useThemeColors();
  const palette: Record<string, { fg: string; bg: string }> = {
    completed: { fg: colors.traffic.green, bg: colors.traffic.greenLight },
    pending: { fg: colors.text.secondary, bg: colors.surface.secondary },
    processing: { fg: colors.primary[600], bg: colors.surface.secondary },
    partial: { fg: colors.traffic.amber, bg: colors.traffic.amberLight },
    failed: { fg: colors.traffic.red, bg: colors.traffic.redLight },
    cancelled: { fg: colors.text.tertiary, bg: colors.surface.secondary },
  };
  const { fg, bg } = palette[status] ?? palette.pending;
  return (
    <View className="rounded-full px-2.5 py-0.5 shrink-0" style={{ backgroundColor: bg }}>
      <Text className="text-xs font-semibold" style={{ color: fg }} numberOfLines={1}>
        {status}
      </Text>
    </View>
  );
}

function Fact({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-start" style={{ gap: 12 }}>
      <Text className="text-sm shrink-0" style={{ color: colors.text.tertiary, width: 112 }} numberOfLines={1}>
        {label}
      </Text>
      <Text
        className="text-sm flex-1"
        style={{ color: colors.text.primary, fontFamily: mono ? 'monospace' : undefined }}
        numberOfLines={2}
        selectable
      >
        {value}
      </Text>
    </View>
  );
}

function Field({
  value,
  onChangeText,
  placeholder,
  invalid = false,
  keyboardType = 'default',
  accessibilityLabel,
}: {
  value: string;
  onChangeText: (v: string) => void;
  placeholder: string;
  invalid?: boolean;
  keyboardType?: 'default' | 'email-address';
  accessibilityLabel: string;
}) {
  const colors = useThemeColors();
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.text.tertiary}
      autoCapitalize="none"
      autoCorrect={false}
      keyboardType={keyboardType}
      accessibilityLabel={accessibilityLabel}
      className="rounded-xl border px-3 text-sm"
      style={noOutlineStyle({
        minHeight: 48,
        color: colors.text.primary,
        backgroundColor: colors.surface.secondary,
        borderColor: invalid ? colors.traffic.red : colors.border.light,
        fontFamily: 'monospace',
      })}
    />
  );
}

function Banner({ tone, text }: { tone: 'error' | 'ok' | 'warn'; text: string }) {
  const colors = useThemeColors();
  const t = {
    error: { fg: colors.traffic.red, bg: colors.traffic.redLight },
    warn: { fg: colors.traffic.amber, bg: colors.traffic.amberLight },
    ok: { fg: colors.traffic.green, bg: colors.traffic.greenLight },
  }[tone];
  return (
    <View className="flex-row items-start rounded-xl px-3 py-2.5" style={{ backgroundColor: t.bg, gap: 8 }}>
      <View className="shrink-0" style={{ paddingTop: 1 }}>
        {tone === 'ok' ? <ShieldCheck size={16} color={t.fg} /> : <AlertTriangle size={16} color={t.fg} />}
      </View>
      <Text className="text-sm flex-1" style={{ color: t.fg }} numberOfLines={4} accessibilityRole="alert">
        {text}
      </Text>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Handle a request
// ─────────────────────────────────────────────────────────────────────────────

type Pending = { immediate: boolean } | null;

function HandleRequest({ onFiled }: { onFiled: (filed: ErasureFiled) => void }) {
  const colors = useThemeColors();
  const lookup = useLookupAccount();
  const file = useFileErasure();
  const [email, setEmail] = useState<string>('');
  const [found, setFound] = useState<ErasureLookup | null>(null);
  const [confirmId, setConfirmId] = useState<string>('');
  const [reason, setReason] = useState<ErasureReason | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);

  const account = found?.account ?? null;
  const open = found?.openRequest ?? null;
  const confirmed = !!account && confirmId.trim().toLowerCase() === account.userId.toLowerCase();
  const ready = confirmed && reason !== null && !file.isPending;
  const openPending = open?.status === 'pending';
  const openRunning = !!open && !openPending;

  const reset = () => {
    setFound(null);
    setConfirmId('');
    setReason(null);
    setError(null);
  };

  const runLookup = () => {
    reset();
    lookup.mutate(email.trim(), {
      onSuccess: (r) => setFound(r),
      onError: (err) => setError(messageFor(err, 'ErasureRequests.lookup')),
    });
  };

  const submit = () => {
    if (!account || !reason || !pending) return;
    const immediate = pending.immediate;
    setPending(null);
    file.mutate(
      { userId: account.userId, confirmUserId: confirmId.trim(), reason, immediate },
      {
        onSuccess: (filed) => {
          onFiled(filed);
          setEmail('');
          reset();
        },
        onError: (err) => setError(messageFor(err, 'ErasureRequests.file')),
      },
    );
  };

  return (
    <Card>
      <SectionTitle
        icon={<Search size={18} color={colors.text.primary} />}
        title="Handle a request"
        hint="Use the address the person wrote to privacy@ from, after verifying it is them."
      />

      <Field
        value={email}
        onChangeText={(v) => { setEmail(v); if (found) reset(); }}
        placeholder="person@example.com"
        keyboardType="email-address"
        accessibilityLabel="Email address of the requester"
      />
      <AccountActionButton
        label={lookup.isPending ? 'Looking up…' : 'Look up account'}
        variant="secondary"
        onPress={runLookup}
        disabled={!email.includes('@')}
        loading={lookup.isPending}
        icon={<Search size={16} color={colors.text.primary} />}
      />

      {error ? <Banner tone="error" text={error} /> : null}

      {found && !account ? (
        <Banner
          tone="warn"
          text="No account uses this address. Tell the person we hold no account for it. A waitlist lead removes themselves with the link in any waitlist email."
        />
      ) : null}

      {account ? (
        <View style={{ gap: 12 }}>
          <View
            className="rounded-xl border"
            style={{ borderColor: colors.border.medium, padding: 12, gap: 8 }}
            accessibilityLabel="Resolved account"
          >
            <Fact label="Account id" value={account.userId} mono />
            <Fact label="Created" value={formatDate(account.createdAt)} />
            <Fact
              label="Open request"
              value={open ? `${open.status} · runs ${formatDate(open.scheduledFor)} · ${open.source}` : 'none'}
            />
          </View>

          {openRunning ? (
            <Banner tone="warn" text="This account's erasure is already running. Follow it in the queue below." />
          ) : (
            <>
              <Text className="text-sm" style={{ color: colors.text.secondary }} numberOfLines={3}>
                Paste the account id above to confirm this is the person who asked.
              </Text>
              <Field
                value={confirmId}
                onChangeText={setConfirmId}
                placeholder="account id"
                invalid={confirmId.length > 0 && !confirmed}
                accessibilityLabel="Confirm the account id"
              />

              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }} numberOfLines={1}>
                Why
              </Text>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {ERASURE_REASONS.map((r) => {
                  const active = reason === r.code;
                  return (
                    <AnimatedPressable
                      key={`reason-${r.code}`}
                      onPress={() => setReason(r.code)}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: active }}
                      className="rounded-full border px-3 py-2"
                      style={{
                        minHeight: 44,
                        justifyContent: 'center',
                        borderColor: active ? colors.primary[600] : colors.border.medium,
                        backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                      }}
                    >
                      <Text
                        className="text-sm"
                        style={{ color: active ? colors.text.inverse : colors.text.primary }}
                        numberOfLines={1}
                      >
                        {r.label}
                      </Text>
                    </AnimatedPressable>
                  );
                })}
              </View>

              <View style={{ gap: 10 }}>
                {!openPending ? (
                  <AccountActionButton
                    label="Schedule (30 days)"
                    variant="primary"
                    onPress={() => setPending({ immediate: false })}
                    disabled={!ready}
                    icon={<CalendarClock size={16} color={colors.text.inverse} />}
                  />
                ) : null}
                <AccountActionButton
                  label={openPending ? 'Expedite: erase immediately' : 'Erase immediately (subject asked)'}
                  variant="destructive"
                  onPress={() => setPending({ immediate: true })}
                  disabled={!ready}
                  icon={<Zap size={16} color={colors.text.inverse} />}
                  accessibilityHint="Only when the person explicitly asked for immediate erasure"
                />
              </View>
            </>
          )}
        </View>
      ) : null}

      <AppModal
        visible={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.immediate ? 'Erase this account now?' : 'Schedule this erasure?'}
      >
        <View style={{ gap: 10 }}>
          <Text className="text-sm" style={{ color: colors.text.primary }}>
            {pending?.immediate
              ? 'The account is erased on the next executor run (daily 02:15 UTC, or a manual dispatch). It cannot be undone, and the person cannot cancel it once it starts.'
              : 'The account is erased in 30 days. The person is emailed the date and can cancel until then by signing in.'}
          </Text>
          <Text className="text-xs" style={{ color: colors.text.tertiary, fontFamily: 'monospace' }} numberOfLines={1} selectable>
            {account?.userId ?? ''}
          </Text>
        </View>
        <AppModal.Actions>
          <ModalAction label="Cancel" onPress={() => setPending(null)} />
          <ModalAction
            label={pending?.immediate ? 'Erase immediately' : 'Schedule'}
            variant="destructive"
            onPress={submit}
          />
        </AppModal.Actions>
      </AppModal>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Closed companies (119-07)
// ─────────────────────────────────────────────────────────────────────────────

type CompanyPending = 'delete' | 'reopen' | 'requeue' | null;

function ClosedCompanies() {
  const colors = useThemeColors();
  const lookup = useLookupCompany();
  const del = useRequestCompanyDataDeletion();
  const reopen = useReopenCompany();
  const requeue = useRequeueCompanyDataDeletion();
  const [idInput, setIdInput] = useState<string>('');
  const [company, setCompany] = useState<CompanyAdminState | null | undefined>(undefined);
  const [confirmId, setConfirmId] = useState<string>('');
  const [reason, setReason] = useState<CompanyDataReason | null>(null);
  const [claimant, setClaimant] = useState<string>('');
  const [confirmClaimant, setConfirmClaimant] = useState<string>('');
  const [pending, setPending] = useState<CompanyPending>(null);
  const [stepUpFor, setStepUpFor] = useState<CompanyPending>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const actions = company ? companyActions(company) : null;
  const confirmed = !!company && confirmId.trim().toLowerCase() === company.startupId.toLowerCase();
  const claimantOk = UUID_RE.test(claimant.trim()) && confirmClaimant.trim().toLowerCase() === claimant.trim().toLowerCase();
  const busy = lookup.isPending || del.isPending || reopen.isPending || requeue.isPending;

  const clearFor = (next?: CompanyAdminState | null) => {
    setCompany(next);
    setConfirmId('');
    setReason(null);
    setClaimant('');
    setConfirmClaimant('');
  };

  const runLookup = (id: string = idInput.trim()) => {
    setError(null);
    lookup.mutate(id, {
      onSuccess: (c) => clearFor(c),
      onError: (err) => {
        clearFor(undefined);
        setError(messageFor(err, 'ErasureRequests.companyLookup'));
      },
    });
  };

  /** A refusal that only needs the step-up opens it; the action is retried after. */
  const failed = (action: Exclude<CompanyPending, null>, context: string) => (err: unknown) => {
    if (err instanceof ErasureAdminError && err.code === 'aal2_required') {
      setStepUpFor(action);
      return;
    }
    setError(messageFor(err, context));
  };

  const run = (action: Exclude<CompanyPending, null>) => {
    if (!company) return;
    setPending(null);
    setError(null);
    setDone(null);
    if (action === 'delete' && reason) {
      del.mutate(
        { startupId: company.startupId, confirmStartupId: confirmId, reason },
        {
          onSuccess: (r) => {
            setDone(`Company-data deletion ${r.created ? 'filed' : 'already filed'} — reference ${r.requestId}. It runs on the next executor pass.`);
            runLookup(company.startupId);
          },
          onError: failed('delete', 'ErasureRequests.companyDataDelete'),
        },
      );
    } else if (action === 'reopen') {
      reopen.mutate(
        { startupId: company.startupId, confirmStartupId: confirmId, claimantUserId: claimant, confirmClaimantUserId: confirmClaimant },
        {
          onSuccess: () => {
            setDone('Reopened. The claimant is the owner again and the company is no longer sealed.');
            runLookup(company.startupId);
          },
          onError: failed('reopen', 'ErasureRequests.companyReopen'),
        },
      );
    } else if (action === 'requeue' && company.dataDeletion) {
      requeue.mutate(company.dataDeletion.id, {
        onSuccess: () => {
          setDone('Re-queued. The data deletion gets five more attempts.');
          runLookup(company.startupId);
        },
        onError: failed('requeue', 'ErasureRequests.companyRequeue'),
      });
    }
  };

  const lc = company?.lifecycle ?? null;
  const modal: Record<Exclude<CompanyPending, null>, { title: string; body: string; label: string }> = {
    delete: {
      title: "Delete this company's data?",
      body:
        'Runs on the next executor pass, with no grace period. Deleted: interview transcripts, data room, claims, analyses and the company texts. Kept: the company page (name, logo, status, website, domain), its score history in full, and its fundraising rounds. A journey snapshot is written first. It cannot be undone.',
      label: 'Delete the data',
    },
    reopen: {
      title: 'Reopen this company?',
      body:
        'The claimant becomes its owner and the company is unsealed. The server requires their own verified claim on the company, made after it closed; nothing else counts as proof.',
      label: 'Reopen',
    },
    requeue: {
      title: 'Re-queue the data deletion?',
      body: 'The failed company-data deletion gets five more attempts on the next executor passes.',
      label: 'Re-queue',
    },
  };

  return (
    <Card>
      <SectionTitle
        icon={<Building2 size={18} color={colors.text.primary} />}
        title="Closed companies"
        hint="Delete a closed company's data on a verified request, reopen it for a returning founder, or re-queue a failed deletion."
      />
      <Field
        value={idInput}
        onChangeText={(v) => { setIdInput(v); if (company !== undefined) clearFor(undefined); }}
        placeholder="company id"
        invalid={idInput.length > 0 && !UUID_RE.test(idInput.trim())}
        accessibilityLabel="Company id"
      />
      <AccountActionButton
        label={lookup.isPending ? 'Looking up…' : 'Look up company'}
        variant="secondary"
        onPress={() => runLookup()}
        disabled={!UUID_RE.test(idInput.trim()) || busy}
        loading={lookup.isPending}
        icon={<Search size={16} color={colors.text.primary} />}
      />

      {error ? <Banner tone="error" text={error} /> : null}
      {done ? <Banner tone="ok" text={done} /> : null}
      {company === null ? <Banner tone="warn" text="No company has this id." /> : null}

      {company ? (
        <View style={{ gap: 12 }}>
          <View className="rounded-xl border" style={{ borderColor: colors.border.medium, padding: 12, gap: 8 }} accessibilityLabel="Resolved company">
            <Fact label="Company id" value={company.startupId} mono />
            <Fact label="Name" value={company.name ?? '—'} />
            <Fact label="Status" value={lc ? `${lc.status} · data ${lc.dataState}` : 'operating'} />
            <Fact label="Closed via" value={lc?.declaredVia ?? '—'} />
            <Fact label="Active members" value={String(company.activeMembers)} />
            <Fact
              label="Data deletion"
              value={company.dataDeletion ? `${company.dataDeletion.status} · ${company.dataDeletion.attempts} attempt(s)` : 'none'}
            />
          </View>

          {!lc ? (
            <Banner tone="warn" text="This company is operating. Nothing here applies to it." />
          ) : lc.dataState === 'deleted' ? (
            <Banner tone="warn" text="Its data is already deleted. The tombstone keeps the status and score history and is never reopened." />
          ) : company.activeMembers > 0 ? (
            <Banner tone="warn" text="This company has an active member, so nothing is done from here." />
          ) : null}

          {actions?.canRequeue ? (
            <AccountActionButton
              label="Re-queue the data deletion"
              variant="secondary"
              onPress={() => setPending('requeue')}
              disabled={busy}
              icon={<RotateCcw size={16} color={colors.text.primary} />}
            />
          ) : null}

          {actions && (actions.canDeleteData || actions.canReopen) ? (
            <>
              <Text className="text-sm" style={{ color: colors.text.secondary }} numberOfLines={3}>
                Paste the company id above to confirm which company this is.
              </Text>
              <Field
                value={confirmId}
                onChangeText={setConfirmId}
                placeholder="company id"
                invalid={confirmId.length > 0 && !confirmed}
                accessibilityLabel="Confirm the company id"
              />
            </>
          ) : null}

          {actions?.canDeleteData ? (
            <View style={{ gap: 10 }}>
              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }} numberOfLines={1}>
                Delete its data — who asked
              </Text>
              <View className="flex-row flex-wrap" style={{ gap: 8 }}>
                {COMPANY_DATA_REASONS.map((r) => {
                  const active = reason === r.code;
                  return (
                    <AnimatedPressable
                      key={`company-reason-${r.code}`}
                      onPress={() => setReason(r.code)}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: active }}
                      className="rounded-full border px-3 py-2"
                      style={{
                        minHeight: 44,
                        justifyContent: 'center',
                        borderColor: active ? colors.primary[600] : colors.border.medium,
                        backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                      }}
                    >
                      <Text className="text-sm" style={{ color: active ? colors.text.inverse : colors.text.primary }} numberOfLines={1}>
                        {r.label}
                      </Text>
                    </AnimatedPressable>
                  );
                })}
              </View>
              <AccountActionButton
                label="Delete the company's data"
                variant="destructive"
                onPress={() => setPending('delete')}
                disabled={!confirmed || !reason || busy}
                icon={<Trash2 size={16} color={colors.text.inverse} />}
              />
            </View>
          ) : null}

          {actions?.canReopen ? (
            <View style={{ gap: 10 }}>
              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }} numberOfLines={1}>
                Reopen for a returning founder
              </Text>
              <Text className="text-sm" style={{ color: colors.text.secondary }} numberOfLines={3}>
                Their account id (look it up above by email), twice. They must have verified the company after it closed.
              </Text>
              <Field
                value={claimant}
                onChangeText={setClaimant}
                placeholder="claimant account id"
                invalid={claimant.length > 0 && !UUID_RE.test(claimant.trim())}
                accessibilityLabel="Claimant account id"
              />
              <Field
                value={confirmClaimant}
                onChangeText={setConfirmClaimant}
                placeholder="claimant account id, again"
                invalid={confirmClaimant.length > 0 && !claimantOk}
                accessibilityLabel="Confirm the claimant account id"
              />
              <AccountActionButton
                label="Reopen the company"
                variant="primary"
                onPress={() => setPending('reopen')}
                disabled={!confirmed || !claimantOk || busy}
                icon={<DoorOpen size={16} color={colors.text.inverse} />}
              />
            </View>
          ) : null}
        </View>
      ) : null}

      <AppModal visible={pending !== null} onClose={() => setPending(null)} title={pending ? modal[pending].title : undefined}>
        <View style={{ gap: 10 }}>
          <Text className="text-sm" style={{ color: colors.text.primary }}>
            {pending ? modal[pending].body : ''}
          </Text>
          <Text className="text-xs" style={{ color: colors.text.tertiary, fontFamily: 'monospace' }} numberOfLines={1} selectable>
            {company?.startupId ?? ''}
          </Text>
        </View>
        <AppModal.Actions>
          <ModalAction label="Cancel" onPress={() => setPending(null)} />
          <ModalAction
            label={pending ? modal[pending].label : ''}
            variant={pending === 'delete' ? 'destructive' : 'primary'}
            onPress={() => pending && run(pending)}
          />
        </AppModal.Actions>
      </AppModal>

      <StepUpModal
        visible={stepUpFor !== null}
        reason="handle a closed company"
        onClose={() => setStepUpFor(null)}
        onVerified={() => {
          const action = stepUpFor;
          setStepUpFor(null);
          if (action) run(action);
        }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. The queue
// ─────────────────────────────────────────────────────────────────────────────

function RequestRow({
  r,
  selected,
  onSelect,
}: {
  r: ErasureRequestSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const colors = useThemeColors();
  return (
    <AnimatedPressable
      onPress={onSelect}
      accessibilityRole="button"
      accessibilityLabel={`Request ${r.id}, ${r.status}`}
      className="rounded-xl border px-3 py-3"
      style={{
        borderColor: selected ? colors.primary[600] : colors.border.light,
        backgroundColor: colors.surface.primary,
        gap: 4,
      }}
    >
      <View className="flex-row items-center" style={{ gap: 8 }}>
        <StatusChip status={r.status} />
        <Text className="text-sm flex-1" style={{ color: colors.text.primary }} numberOfLines={1}>
          {r.status === 'pending' ? `Runs ${formatDate(r.scheduledFor)}` : `Attempt ${r.attempts} of 5`}
        </Text>
        <View className="shrink-0">
          <ChevronRight size={16} color={colors.text.tertiary} />
        </View>
      </View>
      <Text className="text-xs" style={{ color: colors.text.tertiary, fontFamily: 'monospace' }} numberOfLines={1}>
        {r.id}
      </Text>
      {r.lastError ? (
        <Text className="text-xs" style={{ color: colors.traffic.red, fontFamily: 'monospace' }} numberOfLines={2}>
          {r.lastError}
        </Text>
      ) : null}
      {r.holds > 0 ? (
        <Text className="text-xs" style={{ color: colors.traffic.amber }} numberOfLines={1}>
          Holds {r.holds} {r.holds === 1 ? 'company' : 'companies'} unclaimable until it completes
        </Text>
      ) : null}
    </AnimatedPressable>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Proof
// ─────────────────────────────────────────────────────────────────────────────

function countOf(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

/** The plain-text proof to send back to the person. No ids of anyone else. */
function proofText(req: ErasureRequestSummary, proof: { outcome: string; finishedAt: string | null } | null): string {
  if (req.status === 'completed' && proof?.outcome === 'completed') {
    return `Your account and the personal data linked to it were erased on ${formatDate(proof.finishedAt ?? req.completedAt)}. ` +
      `Our reference for this erasure is ${req.id}. If you ever paid us, the payment record is kept in anonymised form for 10 years, as accounting law requires.`;
  }
  if (req.status === 'pending') {
    return `Your erasure request is recorded (reference ${req.id}) and runs on ${formatDate(req.scheduledFor)}.`;
  }
  return `Your erasure request (reference ${req.id}) is being processed.`;
}

function ProofPanel({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const colors = useThemeColors();
  const proofQuery = useErasureProof(requestId);
  const requeue = useRequeueErasure();
  const [confirmRequeue, setConfirmRequeue] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const data = proofQuery.data;
  return (
    <Card>
      <SectionTitle icon={<FileCheck2 size={18} color={colors.text.primary} />} title="Proof of erasure" />
      {proofQuery.isLoading ? (
        <View className="items-center justify-center py-6">
          <ActivityIndicator color={colors.primary[600]} />
        </View>
      ) : proofQuery.isError ? (
        <Banner tone="error" text={messageFor(proofQuery.error, 'ErasureRequests.proof') ?? 'Could not load the proof.'} />
      ) : data ? (
        <View style={{ gap: 12 }}>
          <View className="flex-row items-center" style={{ gap: 8 }}>
            <StatusChip status={data.request.status} />
            <Text className="text-sm flex-1" style={{ color: colors.text.secondary }} numberOfLines={1}>
              {data.request.source === 'admin' ? 'Filed by an admin' : 'Filed by the person'}
              {data.request.reason ? ` · ${data.request.reason}` : ''}
            </Text>
          </View>
          <View style={{ gap: 6 }}>
            <Fact label="Reference" value={data.request.id} mono />
            <Fact label="Requested" value={formatDate(data.request.requestedAt)} />
            <Fact label="Scheduled" value={formatDate(data.request.scheduledFor)} />
            <Fact label="Completed" value={formatDate(data.request.completedAt)} />
            <Fact label="Attempts" value={String(data.request.attempts)} />
            {data.proof ? (
              <>
                <Fact label="Outcome" value={data.proof.outcome} />
                <Fact label="Finished" value={formatDate(data.proof.finishedAt)} />
                <Fact
                  label="Companies"
                  value={`${countOf(data.proof.ownership.transferred)} transferred · ${countOf(data.proof.ownership.unclaimed)} unclaimed · ${countOf(data.proof.ownership.membership_removed)} left`}
                />
                <Fact label="Tables" value={`${Object.keys(data.proof.counts).length} with rows removed or anonymised`} />
                {Object.keys(data.proof.failed).length > 0 ? (
                  <Fact
                    label="Failed steps"
                    value={Object.entries(data.proof.failed).map(([k, v]) => `${k}=${String(v)}`).join(', ')}
                    mono
                  />
                ) : null}
              </>
            ) : (
              <Fact label="Outcome" value="not started yet" />
            )}
          </View>

          <View className="rounded-xl" style={{ backgroundColor: colors.surface.secondary, padding: 12, gap: 6 }}>
            <Text className="text-xs font-semibold" style={{ color: colors.text.tertiary }} numberOfLines={1}>
              Reply to the person
            </Text>
            <Text className="text-sm" style={{ color: colors.text.primary }} selectable>
              {proofText(data.request, data.proof)}
            </Text>
          </View>

          {error ? <Banner tone="error" text={error} /> : null}

          {data.request.status === 'failed' ? (
            <AccountActionButton
              label="Re-queue (5 more attempts)"
              variant="secondary"
              onPress={() => setConfirmRequeue(true)}
              loading={requeue.isPending}
              icon={<RotateCcw size={16} color={colors.text.primary} />}
            />
          ) : null}
          <AccountActionButton label="Close" variant="secondary" onPress={onClose} />
        </View>
      ) : null}

      <AppModal visible={confirmRequeue} onClose={() => setConfirmRequeue(false)} title="Re-queue this erasure?">
        <Text className="text-sm" style={{ color: colors.text.primary }}>
          The next executor run retries it, up to five more times. Fix the cause in its failed steps first, or it will fail
          again. Its companies stay unclaimable until it completes.
        </Text>
        <AppModal.Actions>
          <ModalAction label="Cancel" onPress={() => setConfirmRequeue(false)} />
          <ModalAction
            label="Re-queue"
            variant="primary"
            onPress={() => {
              setConfirmRequeue(false);
              setError(null);
              requeue.mutate(requestId, { onError: (err) => setError(messageFor(err, 'ErasureRequests.requeue')) });
            }}
          />
        </AppModal.Actions>
      </AppModal>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen
// ─────────────────────────────────────────────────────────────────────────────

export default function ErasureRequestsScreen() {
  const colors = useThemeColors();
  const list = useErasureRequests();
  const [selected, setSelected] = useState<string | null>(null);
  const [filed, setFiled] = useState<ErasureFiled | null>(null);
  const [refInput, setRefInput] = useState<string>('');

  return (
    <AppLayout
      title="Erasure Requests"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Erasure Requests' }]}
    >
      <ScreenFrame title="Erasure requests" back={{ fallback: '/(admin)/admin-dashboard' }}>
        <KeyboardAwareBody
          contentContainerStyle={{ paddingHorizontal: isWeb ? 24 : 16, paddingTop: 8, paddingBottom: 48, flexGrow: 1 }}
        >
          <View style={{ width: '100%', maxWidth: MAX_WIDTH, alignSelf: 'center', gap: 16 }}>
            {filed ? (
              <Banner
                tone={filed.failedSteps.length ? 'warn' : 'ok'}
                text={
                  filed.failedSteps.length
                    ? `Recorded (reference ${filed.requestId}), but these steps did not complete: ${filed.failedSteps.join(', ')}. File it again to retry them.`
                    : `${filed.expedited ? 'Expedited' : 'Filed'} — reference ${filed.requestId}, runs ${formatDate(filed.scheduledFor)}.`
                }
              />
            ) : null}

            <HandleRequest onFiled={(f) => { setFiled(f); setSelected(f.requestId); }} />

            {selected ? <ProofPanel requestId={selected} onClose={() => setSelected(null)} /> : null}

            <Card>
              <SectionTitle
                icon={<FileCheck2 size={18} color={colors.text.primary} />}
                title="Find a proof"
                hint="Completed requests leave the queue. Enter the reference to show their proof."
              />
              <Field
                value={refInput}
                onChangeText={setRefInput}
                placeholder="request reference"
                invalid={refInput.length > 0 && !UUID_RE.test(refInput.trim())}
                accessibilityLabel="Request reference"
              />
              <AccountActionButton
                label="Show proof"
                variant="secondary"
                onPress={() => setSelected(refInput.trim())}
                disabled={!UUID_RE.test(refInput.trim())}
              />
            </Card>

            <ClosedCompanies />

            <Card>
              <SectionTitle
                icon={<CalendarClock size={18} color={colors.text.primary} />}
                title="Queue"
                hint="Pending, processing, partial and failed requests. A partial is retried daily; after 5 attempts it fails and the executor run alerts."
              />
              {list.isLoading ? (
                <View className="items-center justify-center py-6">
                  <ActivityIndicator color={colors.primary[600]} />
                </View>
              ) : list.isError ? (
                <View style={{ gap: 10 }}>
                  <Banner tone="error" text={messageFor(list.error, 'ErasureRequests.list') ?? 'Could not load the queue.'} />
                  <AccountActionButton label="Retry" variant="secondary" onPress={() => list.refetch()} />
                </View>
              ) : !list.data || list.data.length === 0 ? (
                <Text className="text-sm" style={{ color: colors.text.tertiary }} numberOfLines={2}>
                  No open requests.
                </Text>
              ) : (
                <View style={{ gap: 8 }}>
                  {list.data.map((r) => (
                    <RequestRow key={`req-${r.id}`} r={r} selected={selected === r.id} onSelect={() => setSelected(r.id)} />
                  ))}
                </View>
              )}
            </Card>
          </View>
        </KeyboardAwareBody>
      </ScreenFrame>
    </AppLayout>
  );
}
